<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAppToast } from '@/shared/lib/useAppToast'

import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { usePagination } from '@/shared/composables/usePagination'

import BrandFormDialog from '@/modules/admin/brands/components/BrandFormDialog.vue'
import DeleteBrandConfirmDialog from '@/modules/admin/brands/components/DeleteBrandConfirmDialog.vue'

import {
  getAllBrands,
  createBrand,
  updateBrand,
  deleteBrand
} from '@/modules/admin/brands/api/brands.service'

const router = useRouter()
const toast = useAppToast()

const brands = ref([])
const loading = ref(false)
const search = ref('')

const formVisible = ref(false)
const formMode = ref('create')
const selectedBrand = ref(null)
const formSubmitting = ref(false)
const brandFieldErrors = ref({})

const deletingBrand = ref(false)
const deleteDialogVisible = ref(false)
const brandToDelete = ref(null)

async function loadBrands() {
  try {
    loading.value = true
    brands.value = await getAllBrands()
  } catch (error) {
    brands.value = []
    toast.error(error.message || 'Не удалось загрузить бренды')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/admin')
}

function openCreateDialog() {
  selectedBrand.value = null
  formMode.value = 'create'
  brandFieldErrors.value = {}
  formVisible.value = true
}

function openEditDialog(brand) {
  selectedBrand.value = { ...brand }
  formMode.value = 'edit'
  brandFieldErrors.value = {}
  formVisible.value = true
}

function closeFormDialog() {
  formVisible.value = false
  selectedBrand.value = null
  brandFieldErrors.value = {}
}

function mapBrandFieldErrors(fieldErrors = {}) {
  const mappedErrors = {}

  for (const [property, message] of Object.entries(fieldErrors)) {
    if (property.toLowerCase() === 'name') {
      mappedErrors.name = message
    }
  }

  return mappedErrors
}

function clearBrandFieldError(field) {
  const remainingErrors = { ...brandFieldErrors.value }
  delete remainingErrors[field]
  brandFieldErrors.value = remainingErrors
}

async function handleSubmitBrand(formData) {
  brandFieldErrors.value = {}
  formSubmitting.value = true

  try {
    if (formMode.value === 'create') {
      await createBrand({
        name: formData.name,
        isActive: formData.isActive
      })

      toast.success('Бренд создан')
    } else if (formMode.value === 'edit' && selectedBrand.value?.id) {
      await updateBrand(selectedBrand.value.id, {
        name: formData.name,
        isActive: formData.isActive
      })

      toast.success('Бренд обновлён')
    }

    closeFormDialog()
    await loadBrands()
  } catch (error) {
    const backendFieldErrors = error?.fieldErrors ?? {}
    brandFieldErrors.value = mapBrandFieldErrors(backendFieldErrors)
    const hasUnhandledFieldErrors = Object.keys(backendFieldErrors)
      .some(property => property.toLowerCase() !== 'name')

    if (!brandFieldErrors.value.name || hasUnhandledFieldErrors) {
      toast.error(error?.message || 'Ошибка при сохранении бренда')
    }
  } finally {
    formSubmitting.value = false
  }
}

function openDeleteDialog(brand) {
  brandToDelete.value = { ...brand }
  deleteDialogVisible.value = true
}

function closeDeleteDialog() {
  if (deletingBrand.value) return
  deleteDialogVisible.value = false
  brandToDelete.value = null
}

async function handleDeleteBrand() {
  if (deletingBrand.value || !brandToDelete.value?.id) return
  deletingBrand.value = true
  try {
    await deleteBrand(brandToDelete.value.id)

    toast.success('Бренд удалён')
    await loadBrands()
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении бренда')
  } finally {
    deletingBrand.value = false
    closeDeleteDialog()
  }
}

const filteredBrands = computed(() => {
  const searchText = search.value.trim().toLowerCase()

  return brands.value.filter(brand => {
    return !searchText || brand.name?.toLowerCase().includes(searchText)
  })
})

onMounted(loadBrands)

const { currentPage, totalPages, paginatedItems: paginatedBrands } = usePagination(filteredBrands)
</script>

<template>
  <div class="admin-brands-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <Button
          label="Создать бренд"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateDialog"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Бренды</h1>
          <p>Управление брендами товаров</p>
        </div>
      </div>

      <div class="filters">
        <div class="filter-group filter-group--search">
          <label>Поиск</label>
          <InputText
            v-model="search"
            placeholder="Поиск по названию бренда"
            class="filter-control"
          />
        </div>
      </div>

      <Card class="brands-card">
        <template #content>
          <div v-if="loading" class="state-block">
            Загрузка...
          </div>

          <div v-else-if="brands.length === 0" class="state-block">
            Список брендов пуст
          </div>

          <div v-else-if="filteredBrands.length === 0" class="state-block">
            По вашему запросу ничего не найдено
          </div>

          <div v-else class="brands-grid">
            <div
              v-for="brand in paginatedBrands"
              :key="brand.id"
              class="brand-card"
            >
              <div class="brand-header">
                <div class="brand-name">{{ brand.name }}</div>
              </div>

              <div class="brand-description">
                {{ brand.isActive ? 'Бренд доступен для использования' : 'Бренд временно неактивен' }}
              </div>

              <div class="brand-meta">
                <span class="meta-chip">
                  {{ brand.isActive ? 'Активен' : 'Неактивен' }}
                </span>
              </div>

              <div class="brand-actions">
                <Button
                  icon="pi pi-pencil"
                  class="icon-btn"
                  outlined
                  title="Редактировать"
                  @click="openEditDialog(brand)"
                />

                <Button
                  icon="pi pi-trash"
                  class="icon-btn icon-btn--danger"
                  outlined
                  title="Удалить"
                  @click="openDeleteDialog(brand)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <BrandFormDialog
        :visible="formVisible"
        :brand="selectedBrand"
        :mode="formMode"
        :submitting="formSubmitting"
        :field-errors="brandFieldErrors"
        @close="closeFormDialog"
        @submit="handleSubmitBrand"
        @clear-field-error="clearBrandFieldError"
      />

      <DeleteBrandConfirmDialog
        :visible="deleteDialogVisible"
        :brand="brandToDelete"
        :deleting="deletingBrand"
        @close="closeDeleteDialog"
        @confirm="handleDeleteBrand"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-brands-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.filter-control {
  width: 100%;
}

.brands-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.brands-card.p-card) {
  background: #ffffff;
}

:deep(.brands-card .p-card-content) {
  padding: 24px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.brand-card {
  background: #ffffff;
  border: 1px solid #ececec;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 10px 24px rgba(17, 17, 17, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.brand-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(17, 17, 17, 0.08);
  border-color: #dddddd;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-name {
  font-weight: 700;
  font-size: 18px;
  color: #111111;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.brand-description {
  font-size: 14px;
  color: #6b6b6b;
  line-height: 1.6;
  min-height: 44px;
}

.brand-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #555555;
  font-size: 12px;
  font-weight: 500;
}

.brand-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

:deep(.p-inputtext) {
  border-radius: 14px;
  border-color: #ddd;
}

:deep(.p-inputtext:focus) {
  border-color: #111;
  box-shadow: 0 0 0 2px rgba(196, 18, 23, 0.08);
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .brands-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .page-shell {
    padding: 16px 12px 32px;
  }

  .page-header h1 {
    font-size: 26px;
  }

  .brand-card {
    padding: 14px;
  }

  .brand-name {
    font-size: 16px;
  }
}
</style>
